import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import client from "@/api/client";
import { toast } from "sonner";
import { Building2, Plus, Trash2, FolderOpen, Link2, ArrowLeft } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

import { PageHeader, SectionCard, MetricCard, CTAButton } from "@/components/ds";
export default function CorporateStructure() {
  const navigate = useNavigate();
  const [structure, setStructure] = useState(null);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateBU, setShowCreateBU] = useState(false);
  const [buForm, setBuForm] = useState({ name: "", description: "", sector: "" });
  const [saving, setSaving] = useState(false);
  const { currentOrg } = useAuth();
  const orgId = currentOrg?.id || 1;

  const load = async () => {
    setLoading(true);
    try {
      const [sRes, aRes] = await Promise.all([
        client.get(`/corporate/orgs/${orgId}/structure`),
        client.get(`/assets?org_id=${orgId}`)
      ]);
      setStructure(sRes.data);
      setAssets(aRes.data);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const createBU = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await client.post(`/corporate/orgs/${orgId}/business-units`, buForm);
      toast.success("Business unit created");
      setShowCreateBU(false);
      setBuForm({ name: "", description: "", sector: "" });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    setSaving(false);
  };

  const deleteBU = async (buId) => {
    if (!window.confirm("Delete this business unit?")) return;
    try { await client.delete(`/corporate/business-units/${buId}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  };

  const assignAsset = async (assetId, buId) => {
    try {
      await client.post(`/corporate/business-units/${buId}/assets/${assetId}`);
      toast.success("Asset assigned");
      load();
    } catch { toast.error("Failed to assign asset"); }
  };

  const unassignAsset = async (assetId) => {
    try {
      await client.post(`/corporate/business-units/0/assets/${assetId}`);
      toast.success("Asset unassigned");
      load();
    } catch { toast.error("Failed"); }
  };

  if (loading) return <div className="p-8"><div className="h-64 bg-white border border-slate-200 rounded-xl animate-pulse" /></div>;

  return (
    <div className="p-6 lg:p-8 space-y-6 animate-fade-in">
      <div className="flex items-start justify-between">
          
      <PageHeader
        title="Asset Structure"
        subtitle="Organize assets into business units for hierarchical reporting."
        backUrl="/corporate"
        backLabel="Corporate Dashboard"
      />
        <button onClick={() => setShowCreateBU(true)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-[#0F172A] text-white text-sm font-medium rounded-lg hover:bg-[#1E293B]"
          data-testid="create-bu-btn">
          <Plus size={14} /> New Business Unit
        </button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-3 gap-4">
        <MetricCard label="Business Units"  value={structure?.totals?.business_units || 0} />
        <MetricCard label="Total Assets"    value={structure?.totals?.total_assets || 0} />
        <MetricCard label="Assigned Assets" value={structure?.totals?.assigned_assets || 0} color="text-emerald-700" />
      </div>

      {/* Business Units */}
      <div className="space-y-4" data-testid="bu-list">
        {(structure?.business_units || []).map(bu => (
          <SectionCard
            key={bu.id}
            title={bu.name}
            subtitle={`${bu.description || bu.sector || "No description"} · ${bu.asset_count || bu.assets?.length || 0} assets`}
            action={<button onClick={() => deleteBU(bu.id)} className="text-slate-300 hover:text-red-500 transition-colors"><Trash2 size={14} /></button>}
            noPadding
          >
              {(bu.assets || []).map(asset => (
                <div key={asset.id} className="flex items-center justify-between p-2.5 bg-slate-50 border border-slate-100 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Building2 size={13} className="text-slate-400" />
                    <p className="text-xs font-medium text-slate-900">{asset.name}</p>
                    {asset.location && <span className="text-xs text-slate-400">· {asset.location}</span>}
                  </div>
                  <button onClick={() => unassignAsset(asset.id)} className="text-xs text-slate-400 hover:text-red-500">Remove</button>
                </div>
              ))}
              {/* Assign unassigned assets to this BU */}
              {(structure?.unassigned_assets || []).length > 0 && (
                <div className="flex items-center gap-2 pt-1">
                  <select onChange={e => e.target.value && assignAsset(parseInt(e.target.value), bu.id)}
                    className="flex-1 h-7 px-2 border border-slate-200 rounded text-xs bg-white" defaultValue="">
                    <option value="">Assign asset to this unit...</option>
                    {(structure?.unassigned_assets || []).map(a => (
                      <option key={a.id} value={a.id}>{a.name}</option>
                    ))}
                  </select>
                </div>
              )}
              {(bu.assets || []).length === 0 && (structure?.unassigned_assets || []).length === 0 && (
                <p className="text-xs text-slate-400 italic py-1">No assets assigned. Add assets in the Assets module first.</p>
              )}
          </SectionCard>
        ))}

        {/* Unassigned assets */}
        {(structure?.unassigned_assets || []).length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
            <p className="text-xs font-semibold text-amber-800 mb-3">Unassigned Assets ({structure.unassigned_assets.length})</p>
            <div className="space-y-2">
              {structure.unassigned_assets.map(asset => (
                <div key={asset.id} className="flex items-center justify-between p-2.5 bg-white border border-amber-200 rounded-lg">
                  <p className="text-xs font-medium text-slate-900">{asset.name}</p>
                  <p className="text-xs text-amber-600">Not assigned to any BU</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {(!structure?.business_units?.length) && (
          <SectionCard>
            <div className="py-10 text-center">
              <FolderOpen size={32} className="mx-auto mb-3 text-slate-200" />
              <p className="text-sm text-slate-500 mb-1">No business units yet</p>
              <p className="text-xs text-slate-400">Create business units to organise your assets for hierarchical emissions reporting.</p>
              <CTAButton variant="ghost" onClick={() => setShowCreateBU(true)} className="mt-4 text-xs text-[#059669]">Create first business unit</CTAButton>
            </div>
          </SectionCard>
        )}
      </div>

      <Dialog open={showCreateBU} onOpenChange={setShowCreateBU}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>New Business Unit</DialogTitle></DialogHeader>
          <form onSubmit={createBU} className="space-y-4 mt-2">
            <div>
              <label className="block text-xs font-medium uppercase tracking-wider text-slate-500 mb-1.5">Unit Name *</label>
              <input type="text" value={buForm.name} onChange={e => setBuForm(f => ({...f, name: e.target.value}))} required
                className="w-full h-10 px-3 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#059669]"
                placeholder="e.g. Manufacturing Operations" data-testid="bu-name-input" />
            </div>
            <div>
              <label className="block text-xs font-medium uppercase tracking-wider text-slate-500 mb-1.5">Description</label>
              <textarea value={buForm.description} onChange={e => setBuForm(f => ({...f, description: e.target.value}))}
                className="w-full p-3 border border-slate-300 rounded-lg text-sm resize-none h-16 focus:outline-none focus:ring-2 focus:ring-[#059669]" />
            </div>
            <div className="flex gap-3">
              <button type="button" onClick={() => setShowCreateBU(false)} className="flex-1 h-10 border border-slate-300 text-sm rounded-lg hover:bg-slate-50">Cancel</button>
              <button type="submit" disabled={saving} className="flex-1 h-10 bg-[#0F172A] text-white text-sm font-medium rounded-lg hover:bg-[#1E293B] disabled:opacity-60" data-testid="bu-submit-btn">
                {saving ? "Creating..." : "Create"}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
